import { create } from 'zustand';
import {
  acknowledgeDrawingSourceRemoved,
  createDrawingNumeral,
  deleteDrawingNumeral,
  discardDrawingFigure,
  getDrawingCanvas,
  getDrawingFigures,
  getDrawingNumerals,
  getDrawingSourceFigures,
  getDrawingTermMappings,
  importDrawingFromIDF,
  patchDrawingNumeral,
  patchDrawingTermMapping,
  reprimeDrawingFigure,
  syncDrawingFigureSource,
  uploadDrawingFigure,
} from '@/api/client';
import type {
  CanvasPrimitive,
  CanvasPrimitiveInput,
  DrawingFigureSummary,
  DrawingFigureFull,
  DrawingNumeralRow,
  DrawingSourceFigure,
  DrawingTermMappingRow,
  GenerateAiJobState,
  NumeralCreateBody,
  NumeralPatchBody,
  VerifyFigureResponse,
} from '@/api/client';
import { getGenerateAiJob, postAcknowledgeGenerateAiJob } from '@/api/client';
import {
  cancelPendingFlush,
  enqueueMutation,
  flushNow,
  getQueueState,
  hydratePendingMutations,
  setOnFlushApplied,
  setOnPersistFailed,
  subscribeToQueue,
} from '@/lib/drawingAutoSave';

export type ReferencePanelTab = 'numerals' | 'claims' | 'terms';

/** Slice 6C — canvas tool palette per the Phase 6 brief's locked
 * decision #8 (cursor / numeral / arrow / text / lasso / zoom; no
 * rectangle/circle/diamond). */
// Phase 3 — 'numeral' tool removed; the chip-placement flow is
// gone (numerals enter the registry via Analyze / auto-map).
// Existing chips on legacy data continue to render via the
// NumeralChip component, unchanged.
export type DrawingTool = 'cursor' | 'arrow' | 'curved_arrow' | 'text' | 'lasso' | 'zoom';

const TAB_STORAGE_KEY = 'drawings:referencePanel.activeTab';

function _readStickyTab(): ReferencePanelTab {
  if (typeof window === 'undefined') return 'numerals';
  try {
    const raw = window.localStorage.getItem(TAB_STORAGE_KEY);
    if (raw === 'numerals' || raw === 'claims' || raw === 'terms') return raw;
    // Migrate legacy 'description' tab persisted by the brief
    // 4-tab build: collapse to 'numerals' so a returning drafter
    // doesn't land on a tab that no longer exists.
    if (raw === 'description') return 'numerals';
  } catch {
    /* localStorage unavailable — fall through */
  }
  return 'numerals';
}

function _writeStickyTab(tab: ReferencePanelTab): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    /* persistence is best-effort */
  }
}

/**
 * Drawing module store (Phase 4).
 *
 * Slice 4A added the lock-status snapshot + header rollup.
 * Slice 4B adds the figures list, source-figures list, and active-
 * figure selection; the latter is local-only in Phase 4 (no backend
 * write yet — Phase 5+ may upgrade it to a per-user persisted value).
 */

export interface DrawingRollup {
  figures_count: number;
  frozen_count: number;
  unanalyzed_count: number;
  issues_count: number;
  active_figure_id: string | null;
  last_activity_at: string | null;
}

export type DrawingHydrationStatus = 'idle' | 'loading' | 'unlocked' | 'locked' | 'error';

interface DrawingStore {
  /** Hydration status for the matter currently open in the drawings view. */
  hydrationStatus: DrawingHydrationStatus;
  /** Reason returned by the backend when the workbench is locked. */
  lockedReason: string | null;
  /** Header rollup — populated only when hydrationStatus === 'unlocked'. */
  rollup: DrawingRollup | null;
  /** Last error message surfaced from the backend (network / 5xx). */
  errorMessage: string | null;
  /** matter id the current snapshot belongs to — guard against stale fetches. */
  hydratedMatterId: string | null;

  /** Drawing-side figures (slice 4B). */
  figures: DrawingFigureSummary[];
  /** Eligible matter_figures awaiting import (slice 4B). */
  sourceFigures: DrawingSourceFigure[];
  /** Drafter's currently-selected figure id. Initially seeded from the
   * rollup's `active_figure_id`; subsequent clicks in the gallery
   * update it locally (no backend write in Phase 4). */
  activeFigureId: string | null;

  /** Cross-figure numeral registry (slice 4D). */
  numerals: DrawingNumeralRow[];
  /** Per-claim term-to-numeral mappings (slice 4D). */
  termMappings: DrawingTermMappingRow[];
  /** Reference panel active tab — persisted across reloads via localStorage. */
  referencePanelTab: ReferencePanelTab;

  /** Slice 5E — transient state per matter for in-flight mutations.
   * `pendingImportSourceIds` is the set of `matter_figures.id` values
   * whose import POST is currently in flight (drives the spinner on
   * AVAILABLE FROM IDF rows). `uploading` is true during the upload
   * round-trip. `pendingSyncIds` is the set of `drawing_figures.id`
   * values whose sync POST is in flight. `mutationError` holds the
   * last actionable error message for inline display. */
  pendingImportSourceIds: Set<number>;
  uploading: boolean;
  pendingSyncIds: Set<string>;
  mutationError: string | null;

  /** Slice 6C — canvas surface state. */
  activeTool: DrawingTool;
  zoomLevel: number;
  /** Per-figure cache of canvas primitives keyed by drawing_figures.id.
   * Hydrated lazily when a figure becomes active; mutations push
   * through both the local cache (for instant UI) and the auto-save
   * queue (for persistence). */
  canvasPrimitives: Record<string, CanvasPrimitive[]>;
  /** HR-P2.2 — figure_updated_at echoed by the canvas GET/POST
   *  endpoint. Cached per figureId on every successful read or
   *  write so ``checkForCrossTabWrite`` can detect another
   *  tab's writes by comparing the server's value on focus
   *  refetch against this cached one. Null entry = no
   *  prior read/write to compare against (fresh mount). */
  figureUpdatedAtCache: Record<string, string | null>;
  /** Queue + save status surfaced from src/lib/drawingAutoSave so the
   * header indicator can render `auto-saved · 2:18 PM` /
   * `saving…` / `working offline · N changes queued`. */
  saveStatus: 'idle' | 'saving' | 'saved' | 'offline';
  pendingMutationCount: number;
  lastSavedAt: number | null;

  /** Slice 6F — per-figure undo/redo stacks. Each snapshot is a
   * deep clone of the figure's primitives at a mutation boundary;
   * capped at 50 snapshots per figure (brief §7). Persisted to
   * localStorage on every mutation so the stack survives reloads
   * (the brief calls for IndexedDB; localStorage is the simpler
   * Phase 6 fallback — sizes fit comfortably under the 5 MB cap.
   * Switch to IndexedDB if Phase 6+ feedback shows quota pressure). */
  undoStacks: Record<string, { past: CanvasPrimitive[][]; future: CanvasPrimitive[][] }>;
  /** When set, ongoing mutations skip the per-mutation snapshot —
   * a drag burst gets ONE snapshot at pointerdown rather than one
   * per pointermove. PointerDown sets this; PointerUp / commit
   * clears it. */
  isMutationGrouped: boolean;

  /** Slice 6G — cross-component requests that flow from the
   * ReferencePanel to the Canvas without coupling them directly:
   *  - `pendingPlaceRequest` opens the numeral-place modal at the
   *    given canvas coords (the "+ New" button on the Numerals tab
   *    pins it to the canvas centre).
   *  - `numeralFocusRequest` asks the Canvas to scroll-into-view
   *    + briefly highlight the chip. The `ts` timestamp ensures
   *    repeat clicks on the same row re-trigger the effect even
   *    though the figureId / primitiveId pair is unchanged. */
  pendingPlaceRequest: { x: number; y: number; ts: number } | null;
  numeralFocusRequest: { figureId: string; primitiveId: string; ts: number } | null;
  // Numeral-placement state moved to placementMode +
  // pendingNumeralId/Label below — see Cue Part 2 rewrite.

  /** Cue Part 2 — interaction-layer rewrite. Selection state is
   * the source of truth for the lasso + delete affordances; lives
   * in the store so the floating delete chrome and the Numerals
   * panel can both observe it without prop-drilling through the
   * Canvas component. Empty array = nothing selected. */
  selectedPrimitiveIds: string[];
  // Konva chunk-3 followup — bumped by every endpoint that
  // changes a figure's persisted SVG bytes (vectorize, SVG
  // patch). KonvaCanvas's useFigureSvg hook reads the
  // per-figure counter as its re-fetch signal so canvas-write
  // bumps to figure.updated_at don't trigger a vector layer
  // refetch (the chunk-3 "vector flash" the followup fixes).
  svgRefreshByFigure: Record<string, number>;
  // Phase 3 — placementMode / pendingNumeralId / pendingNumeralLabel
  // state retired with the # canvas tool. Numerals enter the
  // registry via Analyze (vision extraction) or auto-map (text-
  // annotation pass landing in Phase 4); the click-arm-drop flow
  // is gone.

  setHydrating: (matterId: string) => void;
  setLocked: (matterId: string, reason: string) => void;
  setUnlocked: (matterId: string, rollup: DrawingRollup) => void;
  setError: (matterId: string, message: string) => void;
  setFigures: (figures: DrawingFigureSummary[]) => void;
  /** CHUNK 1 — merge a patched figure row from the PATCH endpoint
   * into the local figures array without dropping the rest of
   * the cache. Cheap: replaces the matching id by reference. */
  upsertFigureFromPatch: (figure: DrawingFigureSummary) => void;
  setSourceFigures: (sourceFigures: DrawingSourceFigure[]) => void;
  setActiveFigureId: (figureId: string | null) => void;
  setNumerals: (numerals: DrawingNumeralRow[]) => void;
  setTermMappings: (termMappings: DrawingTermMappingRow[]) => void;
  setReferencePanelTab: (tab: ReferencePanelTab) => void;
  setMutationError: (message: string | null) => void;

  /** Refresh all matter-scoped read endpoints in parallel. Used by
   * mutation actions after a successful import / upload / sync /
   * discard so the gallery + reference panel stay in lockstep with
   * the backend. */
  refreshLists: (matterId: string) => Promise<void>;
  /** Mutation actions — Phase 5E. Each manages its own pending-set
   * entry so the UI can render per-row spinners. */
  importFromIdf: (matterId: string, sourceMatterFigureId: number) => Promise<DrawingFigureFull | null>;
  uploadFigure: (matterId: string, file: File) => Promise<DrawingFigureFull | null>;
  syncSource: (matterId: string, drawingFigureId: string) => Promise<DrawingFigureFull | null>;
  discardFigure: (matterId: string, drawingFigureId: string) => Promise<boolean>;
  reprimeFigure: (matterId: string, drawingFigureId: string) => Promise<DrawingFigureFull | null>;
  acknowledgeSourceRemoved: (matterId: string, drawingFigureId: string) => Promise<DrawingFigureFull | null>;

  /** Slice 6C — canvas + tool actions. */
  setActiveTool: (tool: DrawingTool) => void;
  setZoom: (level: number) => void;
  /** Hydrate the canvas-primitives cache for a figure. Idempotent —
   * a no-op when the cache already holds data for the figure. */
  loadCanvasFor: (matterId: string, figureId: string, force?: boolean) => Promise<void>;
  /** Local-then-queue mutations. The store mutates the cache so the
   * UI updates instantly; the auto-save queue collapses bursts and
   * eventually POSTs. */
  upsertCanvasPrimitive: (
    matterId: string,
    figureId: string,
    primitive: CanvasPrimitiveInput,
  ) => string;
  removeCanvasPrimitive: (
    matterId: string,
    figureId: string,
    primitiveId: string,
  ) => void;
  /** Bulk variant — wraps a single mutation group around the loop so
   * undo restores all deletions in one step instead of N. Used by the
   * canvas Delete-key handler + the floating selection action bar. */
  deleteSelectedPrimitives: (
    matterId: string,
    figureId: string,
    primitiveIds: string[],
  ) => void;
  replaceCanvasState: (
    matterId: string,
    figureId: string,
    primitives: CanvasPrimitiveInput[],
  ) => void;
  /** Force an immediate flush — used on figure switch / blur and
   * before navigation away from the workbench. */
  flushCanvasNow: () => Promise<void>;

  /** Slice 6F — undo / redo. */
  beginCanvasMutationGroup: (matterId: string, figureId: string) => void;
  endCanvasMutationGroup: () => void;
  undoCanvas: (matterId: string, figureId: string) => void;
  redoCanvas: (matterId: string, figureId: string) => void;
  /** Surfaced for the toolbar so the buttons can disable when the
   * stack is empty for the active figure. */
  canUndo: (figureId: string) => boolean;
  canRedo: (figureId: string) => boolean;

  /** Slice 6G — cross-component bridges between the panel and
   * canvas. */
  requestNumeralPlaceAt: (x: number, y: number) => void;
  clearNumeralPlaceRequest: () => void;
  requestNumeralFocus: (figureId: string, primitiveId: string) => void;
  clearNumeralFocusRequest: () => void;
  // armNumeralForPlacement / clearArmedNumeral replaced by
  // startNumeralPlacement / cancelNumeralPlacement (Cue Part 5).

  /** Cue Part 2 — selection + placement actions for the rewritten
   * interaction layer. ``setSelectedIds`` replaces a list outright;
   * ``clearSelection`` is the canonical empty-selection reset.
   * ``deletePrimitives`` removes ids from the local cache + the
   * autosave queue inside one mutation group so undo restores them
   * in one step. The numeral-placement trio
   * (start/cancel/placeNumeral) replaces the older armedNumeralId
   * flow with a bookkeeping shape that drives the placement banner
   * without a numerals-array lookup at render time. */
  setSelectedIds: (ids: string[]) => void;
  /** Bump the per-figure SVG re-fetch counter. Called by the
   *  vectorize success path (ActionBench) and after any SVG
   *  PATCH (the chunk-3-followup vector minor-edits flow). */
  bumpSvgRefresh: (figureId: string) => void;
  clearSelection: () => void;
  deletePrimitives: (
    matterId: string, figureId: string, primitiveIds: string[],
  ) => void;
  // Phase 3 — startNumeralPlacement / cancelNumeralPlacement /
  // placeNumeral retired. Numerals enter the registry via Analyze
  // / auto-map; no canvas-click placement flow remains.

  /** Numeral registry mutations (slice 6B endpoints). */
  createNumeralRegistryRow: (matterId: string, body: NumeralCreateBody) => Promise<DrawingNumeralRow | null>;
  patchNumeralRegistryRow: (matterId: string, numeralId: string, body: NumeralPatchBody) => Promise<DrawingNumeralRow | null>;
  deleteNumeralRegistryRow: (matterId: string, numeralId: string) => Promise<boolean>;

  /** PD-002 — bind a claim term to a registry numeral. Drives the
   *  Claims-tab guided mapping panel; updates termMappings + the
   *  matching drawing_numerals row's bound_claim_term in place so
   *  the rail badges update without a refetch. Returns true on
   *  success, false on API error. */
  linkTermToNumeral: (
    matterId: string,
    termMappingId: string,
    numeralId: string | null,
  ) => Promise<boolean>;

  /** Slice 7G — AI generation modal visibility. The job lifecycle
   * (job_id + polling + candidate state) lives inside the modal
   * component itself so the store stays event-loop-clean; only the
   * open/closed flag needs to be cross-component (FigureGallery
   * opens it, DrawingPage mounts it). */
  generationModalOpen: boolean;
  openGenerationModal: () => void;
  closeGenerationModal: () => void;

  /** BUG-060 — AI figure-generation job state lives in the store
   * so closing the modal doesn't tear down the polling loop or
   * lose progress. The header pill watches these fields to render
   * a persistent indicator while a job runs in the background. */
  activeGenerationJobId: string | null;
  generationJobStatus:
    | 'idle' | 'queued' | 'running'
    | 'completed' | 'failed' | 'cancelled';
  /** Latest poll snapshot. Mirrors the API's GenerateAiJobState
   * shape so the modal can hydrate from this directly without a
   * field-rename layer. */
  generationJobState: GenerateAiJobState | null;
  /** Matter id the active job belongs to — used by the polling
   * loop to short-circuit when the drafter switches matters
   * mid-job. */
  generationJobMatterId: string | null;
  setGenerationJob: (
    matterId: string, jobId: string, initial?: GenerateAiJobState,
  ) => void;
  updateGenerationProgress: (state: GenerateAiJobState) => void;
  clearGenerationJob: () => void;
  /** Session-scoped pill dismissal — clicking the X on the
   *  GenerationJobPill stashes the job_id here so the pill
   *  doesn't reappear for that job until the drafter either
   *  reloads OR a NEW job is seeded. Combined with the
   *  available-count auto-hide (consumed === total → no toast),
   *  this gives the drafter both an explicit close and a
   *  natural disappearance once every candidate is committed. */
  dismissedGenerationJobIds: Set<string>;
  dismissGenerationJobPill: (jobId: string) => void;
  /** Start the background poll loop for the currently-active
   * job. Idempotent — repeated calls reuse the existing
   * interval. */
  startPollingGenerationJob: () => void;
  /** Stop the background poll loop. Called on terminal status,
   * matter switch, or explicit drafter dismiss. */
  stopPollingGenerationJob: () => void;

  /** Slice 8D — Drafter's-Note (Analyze) modal visibility. Carries
   * the figure id so DrawingPage can render the modal scoped to a
   * specific figure (the action bench in slice 8E will set it from
   * the active figure). The actual analyze call + bundle response
   * live inside the modal component. */
  analyzeModalFigureId: string | null;
  openAnalyzeModal: (figureId: string) => void;
  closeAnalyzeModal: () => void;

  /** Slice 9E — Claim edit modal visibility. Carries the claim id
   * the right-panel ClaimsTab clicked "Edit" on. The modal owns
   * the edit / rerun-impact / revert flow against the four Phase 9
   * backend endpoints (slice 9B/9C). */
  claimEditModalClaimId: string | null;
  openClaimEditModal: (claimId: string) => void;
  closeClaimEditModal: () => void;

  /** Slice 8E.1 — latest verify result for the active figure. The
   * action bench reads this to render issue badges; status strip
   * + reference panel will read the same in slice 8F. Refreshed
   * by every Verify-button click. Read by StatusStrip for the
   * "N verify issues" / "✓ verify clean" count + the click-to-
   * popover surface. The VerifyFindingsPanel auto-popup +
   * dismiss-sig dance was retired (BUG-DRAW-007); the count
   * + popover replace it. */
  latestVerifyByFigure: Record<string, VerifyFigureResponse>;
  /** True while the verify POST is in flight for the active
   * figure. The action bench's Verify button shows a spinner. */
  verifyingFigureId: string | null;
  setLatestVerifyResult: (figureId: string, result: VerifyFigureResponse) => void;
  setVerifyingFigureId: (figureId: string | null) => void;
  /** Drop the verify-result entry for a figure. Called on
   *  figure switch so a stale result from a prior figure
   *  doesn't shadow the freshly-loaded one's status badge. */
  clearVerifyState: (figureId: string) => void;

  /** Slice 8E.2.a — transient toast for action-bench success
   * messages (Auto-map results, etc). DrawingPage renders a
   * small bottom-right snackbar; the action calls `showToast`
   * which auto-clears after 5 s. Distinct from `mutationError`
   * (which is for error states + persistent banners). */
  toastMessage: string | null;
  showToast: (message: string) => void;
  clearToast: () => void;

  reset: () => void;
}

// FT-008 (C1) — module-scoped in-flight canvas-load registry. Lives
// outside the Zustand state on purpose: this is transient bookkeeping
// (cleared the instant each promise settles), not persistent state,
// and stashing it in the store would trigger needless re-renders on
// every subscriber when entries are added/removed. Key: figureId.
const _inflightCanvasLoads: Record<string, Promise<void>> = {};

export const useDrawingStore = create<DrawingStore>((set) => ({
  hydrationStatus: 'idle',
  lockedReason: null,
  rollup: null,
  errorMessage: null,
  hydratedMatterId: null,
  figures: [],
  sourceFigures: [],
  activeFigureId: null,
  numerals: [],
  termMappings: [],
  referencePanelTab: _readStickyTab(),
  pendingImportSourceIds: new Set<number>(),
  uploading: false,
  pendingSyncIds: new Set<string>(),
  mutationError: null,
  activeTool: 'cursor',
  zoomLevel: 1,
  canvasPrimitives: {},
  figureUpdatedAtCache: {},
  saveStatus: 'idle',
  pendingMutationCount: 0,
  lastSavedAt: null,
  undoStacks: {},
  isMutationGrouped: false,
  pendingPlaceRequest: null,
  numeralFocusRequest: null,
  selectedPrimitiveIds: [],
  svgRefreshByFigure: {},
  generationModalOpen: false,
  activeGenerationJobId: null,
  generationJobStatus: 'idle',
  generationJobState: null,
  generationJobMatterId: null,
  // BUG-003 quick win — hydrate dismissed-job set from
  // sessionStorage so a Drawing-module remount (Spec → Drawing)
  // doesn't re-surface a "Generation cancelled" toast the
  // drafter already dismissed. Safe inline IIFE; storage errors
  // fall back to an empty set.
  dismissedGenerationJobIds: ((): Set<string> => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return new Set<string>();
      }
      const raw = window.sessionStorage.getItem('drawing.dismissed_gen_job_ids');
      if (!raw) return new Set<string>();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set<string>();
      return new Set<string>(parsed.filter((x): x is string => typeof x === 'string'));
    } catch {
      return new Set<string>();
    }
  })(),

  openGenerationModal: () => set({ generationModalOpen: true, mutationError: null }),
  closeGenerationModal: () => set({ generationModalOpen: false }),

  // ── BUG-060 — generation-job state actions ─────────────────────
  setGenerationJob: (matterId, jobId, initial) => {
    const state = useDrawingStore.getState();
    // BUG-003 — the dismissal set is the SOURCE OF TRUTH for
    // "drafter saw this job and clicked X". Previously this
    // action deleted the jobId from the set on every seed, which
    // defeated the dismissal: DrawingPage.hydrate calls
    // getActiveGenerateAiJob → setGenerationJob on every mount,
    // and the same cancelled job kept reappearing.
    //
    // Server-side acknowledged_at + the age filter below are the
    // primary defences; sessionStorage dismissal stays as
    // session-scoped belt-and-braces. UUID collision is
    // mathematically negligible (random v4 collision probability
    // is ~10^-18) so there is no realistic scenario where
    // preserving the dismissal harms a legitimate new job.
    //
    // ALSO BUG-003 — terminal-status jobs older than the cutoff
    // (10 minutes) are auto-acknowledged in the FE even if the
    // server didn't filter them (older deploy, race between
    // mount and acknowledge POST). Without this guard a
    // cancelled job from yesterday would still flash the toast
    // for the first paint before the ack POST round-trips.
    const isTerminal = initial && (
      initial.status === 'cancelled'
      || initial.status === 'failed'
      || initial.status === 'completed'
    );
    const ackedServerSide = !!(initial && initial.acknowledged_at);
    const createdAt = initial?.created_at ? Date.parse(initial.created_at) : 0;
    const ageMs = createdAt > 0 ? Date.now() - createdAt : 0;
    const STALE_TERMINAL_MS = 10 * 60 * 1000;
    const isStaleCancellation =
      isTerminal
      && initial?.status !== 'completed'
      && ageMs > STALE_TERMINAL_MS;
    if (ackedServerSide || isStaleCancellation) {
      // Auto-dismiss this seed — don't surface the pill, don't
      // start polling. The active-job state is updated so any
      // UI that needs to know the latest job still sees it, but
      // the dismissedGenerationJobIds set carries the
      // suppression.
      const nextDismissed = new Set<string>(state.dismissedGenerationJobIds);
      nextDismissed.add(jobId);
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(
            'drawing.dismissed_gen_job_ids',
            JSON.stringify(Array.from(nextDismissed)),
          );
        }
      } catch { /* storage unavailable */ }
      set({
        activeGenerationJobId: jobId,
        generationJobMatterId: matterId,
        generationJobStatus: initial?.status ?? 'queued',
        generationJobState: initial ?? null,
        dismissedGenerationJobIds: nextDismissed,
      });
      return;
    }
    set({
      activeGenerationJobId: jobId,
      generationJobMatterId: matterId,
      generationJobStatus: initial?.status ?? 'queued',
      generationJobState: initial ?? null,
      // Preserve the existing dismissal set — see comment above.
      dismissedGenerationJobIds: state.dismissedGenerationJobIds,
    });
    state.startPollingGenerationJob();
  },
  dismissGenerationJobPill: (jobId) => {
    set((s) => {
      const next = new Set(s.dismissedGenerationJobIds);
      next.add(jobId);
      // sessionStorage persistence is the in-tab fallback so a
      // Spec → Drawing remount doesn't re-surface the pill for
      // the SAME tab + SAME session. The cross-session
      // suppression now rides on the server's acknowledged_at
      // column (see acknowledge POST below); sessionStorage is
      // only the belt-and-braces layer for the same-session
      // case. Try/catch wraps every storage call because
      // Safari/private-mode and overflow throw.
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(
            'drawing.dismissed_gen_job_ids',
            JSON.stringify(Array.from(next)),
          );
        }
      } catch { /* storage unavailable — in-memory dismissal still works */ }
      return { dismissedGenerationJobIds: next };
    });
    // BUG-003 — server-side acknowledge. Fire-and-forget; a
    // network failure does NOT block local dismissal (the
    // session-scoped set above already hid the toast). Without
    // this, the cancelled toast would re-surface on the NEXT
    // browser session even after the drafter dismissed it,
    // because sessionStorage doesn't survive a tab close.
    const state = useDrawingStore.getState();
    const matterId = state.generationJobMatterId;
    if (matterId) {
      void postAcknowledgeGenerateAiJob(matterId, jobId).catch((e) => {
        // Non-fatal — local dismiss already happened. Log only.
        // eslint-disable-next-line no-console
        console.warn(
          '[drawing] acknowledge POST failed for job %s; '
          + 'session-scoped dismissal still applies. %o',
          jobId, e,
        );
      });
    }
  },
  updateGenerationProgress: (next) =>
    set((s) => {
      // Chunk-2 redesign: auto-open the candidate-selection modal
      // ONLY on the in-page running→completed transition (i.e. the
      // worker finished while the drafter is on the page). The
      // page-reload auto-open in DrawingPage.hydrate was retired
      // in favour of the Recent Generations panel surface.
      const prev = s.generationJobStatus;
      const justCompleted =
        next.status === 'completed' && prev === 'running';
      return {
        generationJobStatus: next.status,
        generationJobState: next,
        ...(justCompleted ? { generationModalOpen: true } : {}),
      };
    }),
  clearGenerationJob: () => {
    useDrawingStore.getState().stopPollingGenerationJob();
    set({
      activeGenerationJobId: null,
      generationJobStatus: 'idle',
      generationJobState: null,
      generationJobMatterId: null,
    });
  },
  startPollingGenerationJob: () => _startGenerationJobPoll(),
  stopPollingGenerationJob: () => _stopGenerationJobPoll(),

  analyzeModalFigureId: null,
  openAnalyzeModal: (figureId) =>
    set({ analyzeModalFigureId: figureId, mutationError: null }),
  closeAnalyzeModal: () => set({ analyzeModalFigureId: null }),

  claimEditModalClaimId: null,
  openClaimEditModal: (claimId) =>
    set({ claimEditModalClaimId: claimId, mutationError: null }),
  closeClaimEditModal: () => set({ claimEditModalClaimId: null }),

  latestVerifyByFigure: {},
  verifyingFigureId: null,
  setLatestVerifyResult: (figureId, result) =>
    set((s) => ({
      latestVerifyByFigure: { ...s.latestVerifyByFigure, [figureId]: result },
    })),
  setVerifyingFigureId: (figureId) => set({ verifyingFigureId: figureId }),
  clearVerifyState: (figureId) =>
    set((s) => {
      if (!(figureId in s.latestVerifyByFigure)) return s;
      const nextResults = { ...s.latestVerifyByFigure };
      delete nextResults[figureId];
      return { latestVerifyByFigure: nextResults };
    }),

  toastMessage: null,
  showToast: (message) => {
    set({ toastMessage: message });
    // 5 s auto-clear. Re-firing showToast resets the timer because
    // the new setState replaces the message and the existing timer
    // (if any) just becomes a no-op when toastMessage is null at
    // its tick.
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        const cur = useDrawingStore.getState().toastMessage;
        if (cur === message) set({ toastMessage: null });
      }, 5000);
    }
  },
  clearToast: () => set({ toastMessage: null }),

  setHydrating: (matterId) =>
    set({
      hydrationStatus: 'loading',
      lockedReason: null,
      rollup: null,
      errorMessage: null,
      hydratedMatterId: matterId,
      figures: [],
      sourceFigures: [],
      activeFigureId: null,
      numerals: [],
      termMappings: [],
    }),
  setLocked: (matterId, reason) =>
    set({
      hydrationStatus: 'locked',
      lockedReason: reason,
      rollup: null,
      errorMessage: null,
      hydratedMatterId: matterId,
      figures: [],
      sourceFigures: [],
      activeFigureId: null,
      numerals: [],
      termMappings: [],
    }),
  setUnlocked: (matterId, rollup) =>
    set({
      hydrationStatus: 'unlocked',
      lockedReason: null,
      rollup,
      errorMessage: null,
      hydratedMatterId: matterId,
      // Seed selection from the rollup; the gallery overrides it on click.
      activeFigureId: rollup.active_figure_id,
    }),
  setError: (matterId, message) =>
    set({
      hydrationStatus: 'error',
      lockedReason: null,
      rollup: null,
      errorMessage: message,
      hydratedMatterId: matterId,
    }),
  setFigures: (figures) => set({ figures }),
  upsertFigureFromPatch: (figure) => set((state) => ({
    figures: state.figures.map((f) => (f.id === figure.id ? figure : f)),
  })),
  setSourceFigures: (sourceFigures) => set({ sourceFigures }),
  setActiveFigureId: (figureId) => {
    // Drop verify state for the figure we're leaving so a fresh
    // Verify click on the NEW figure isn't shadowed by an old
    // result still cached in latestVerifyByFigure under the
    // previous figure id. Also clears the dismissal sig so the
    // panel re-renders cleanly if the drafter switches back.
    const prev = useDrawingStore.getState().activeFigureId;
    if (prev && prev !== figureId) {
      useDrawingStore.getState().clearVerifyState(prev);
    }
    set({ activeFigureId: figureId });
  },
  setNumerals: (numerals) => set({ numerals }),
  setTermMappings: (termMappings) => set({ termMappings }),
  setReferencePanelTab: (tab) => {
    _writeStickyTab(tab);
    set({ referencePanelTab: tab });
  },
  setMutationError: (message) => set({ mutationError: message }),

  refreshLists: async (matterId) => {
    const [figures, source, numerals, termMappings] = await Promise.all([
      getDrawingFigures(matterId),
      getDrawingSourceFigures(matterId),
      getDrawingNumerals(matterId),
      getDrawingTermMappings(matterId),
    ]);
    if (Array.isArray(figures)) set({ figures });
    if (Array.isArray(source)) set({ sourceFigures: source });
    if (Array.isArray(numerals)) set({ numerals });
    if (Array.isArray(termMappings)) set({ termMappings });
  },

  importFromIdf: async (matterId, sourceMatterFigureId) => {
    const cur = useDrawingStore.getState().pendingImportSourceIds;
    if (cur.has(sourceMatterFigureId)) return null;
    set({
      mutationError: null,
      pendingImportSourceIds: new Set([...cur, sourceMatterFigureId]),
    });
    try {
      const r = await importDrawingFromIDF(matterId, sourceMatterFigureId);
      await useDrawingStore.getState().refreshLists(matterId);
      // Auto-select the newly imported figure so the canvas updates.
      set({ activeFigureId: r.figure.id });
      // Surface a soft inline note when the LLM call failed but the
      // import landed. Drafter sees the figure appear with the
      // "Priming failed" banner; no toast spam.
      if (r.figure.prime_error) {
        set({ mutationError: `Import landed, but priming failed: ${r.figure.prime_error}` });
      }
      return r.figure;
    } catch (e: unknown) {
      const errResp = (e as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      const detail = errResp?.data?.detail as
        | string
        | { error_code?: string; detail?: string; drawing_figure_id?: string }
        | undefined;
      if (errResp?.status === 409 && typeof detail === 'object' && detail?.error_code === 'already_imported') {
        // Switch the selection to the already-imported figure so the
        // drafter doesn't get stuck.
        if (detail.drawing_figure_id) {
          set({ activeFigureId: detail.drawing_figure_id });
          await useDrawingStore.getState().refreshLists(matterId);
        }
        set({ mutationError: 'This figure was already imported.' });
        return null;
      }
      if (typeof detail === 'object' && detail?.error_code === 'source_unavailable') {
        set({
          mutationError:
            'This figure was discarded from the IDF and can\'t be imported. '
            + 'Restore it in Stage 1 first.',
        });
        return null;
      }
      const message = (typeof detail === 'string' && detail)
        || (typeof detail === 'object' && detail?.detail)
        || (e as { message?: string })?.message
        || 'Import failed.';
      set({ mutationError: String(message) });
      return null;
    } finally {
      const cur2 = useDrawingStore.getState().pendingImportSourceIds;
      // ``new Set(iterable)`` widens to Set<unknown> under TS' es5
      // target without downlevelIteration; preserve the Set<number>
      // generic explicitly via Array.from + spread.
      const next = new Set<number>(Array.from(cur2));
      next.delete(sourceMatterFigureId);
      set({ pendingImportSourceIds: next });
    }
  },

  uploadFigure: async (matterId, file) => {
    if (useDrawingStore.getState().uploading) return null;
    set({ uploading: true, mutationError: null });
    try {
      const r = await uploadDrawingFigure(matterId, file);
      await useDrawingStore.getState().refreshLists(matterId);
      set({ activeFigureId: r.figure.id });
      if (r.figure.prime_error) {
        set({ mutationError: `Upload landed, but priming failed: ${r.figure.prime_error}` });
      }
      return r.figure;
    } catch (e: unknown) {
      const errResp = (e as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      const detail = errResp?.data?.detail as
        | string
        | { error_code?: string; detail?: string }
        | undefined;
      if (errResp?.status === 413
          || (typeof detail === 'object' && detail?.error_code === 'upload_too_large')) {
        set({ mutationError: 'File is too large.' });
        return null;
      }
      if (typeof detail === 'object' && detail?.error_code === 'unsupported_image_mime') {
        set({ mutationError: 'Only PNG, JPEG, GIF, and WebP images are accepted.' });
        return null;
      }
      const message = (typeof detail === 'string' && detail)
        || (typeof detail === 'object' && detail?.detail)
        || (e as { message?: string })?.message
        || 'Upload failed.';
      set({ mutationError: String(message) });
      return null;
    } finally {
      set({ uploading: false });
    }
  },

  syncSource: async (matterId, drawingFigureId) => {
    const cur = useDrawingStore.getState().pendingSyncIds;
    if (cur.has(drawingFigureId)) return null;
    set({
      mutationError: null,
      pendingSyncIds: new Set([...cur, drawingFigureId]),
    });
    try {
      const r = await syncDrawingFigureSource(matterId, drawingFigureId);
      await useDrawingStore.getState().refreshLists(matterId);
      if (r.figure.prime_error) {
        set({ mutationError: `Description synced, but re-priming failed: ${r.figure.prime_error}` });
      }
      return r.figure;
    } catch (e: unknown) {
      const errResp = (e as { response?: { data?: { detail?: unknown } } })?.response;
      const detail = errResp?.data?.detail as
        | string
        | { error_code?: string; detail?: string }
        | undefined;
      const message = (typeof detail === 'string' && detail)
        || (typeof detail === 'object' && detail?.detail)
        || (e as { message?: string })?.message
        || 'Sync failed.';
      set({ mutationError: String(message) });
      return null;
    } finally {
      const cur2 = useDrawingStore.getState().pendingSyncIds;
      // Preserve Set<string> generic — see pendingImportSourceIds
      // sibling above for the rationale.
      const next = new Set<string>(Array.from(cur2));
      next.delete(drawingFigureId);
      set({ pendingSyncIds: next });
    }
  },

  reprimeFigure: async (matterId, drawingFigureId) => {
    const cur = useDrawingStore.getState().pendingSyncIds;
    if (cur.has(drawingFigureId)) return null;
    set({
      mutationError: null,
      pendingSyncIds: new Set([...cur, drawingFigureId]),
    });
    try {
      const r = await reprimeDrawingFigure(matterId, drawingFigureId);
      await useDrawingStore.getState().refreshLists(matterId);
      if (r.figure.prime_error) {
        set({ mutationError: `Re-priming failed: ${r.figure.prime_error}` });
      }
      return r.figure;
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const message = (typeof detail === 'string' && detail)
        || (typeof detail === 'object' && detail !== null && (detail as { detail?: string }).detail)
        || (e as { message?: string })?.message
        || 'Reprime failed.';
      set({ mutationError: String(message) });
      return null;
    } finally {
      const cur2 = useDrawingStore.getState().pendingSyncIds;
      // Preserve Set<string> generic — see pendingImportSourceIds
      // sibling above for the rationale.
      const next = new Set<string>(Array.from(cur2));
      next.delete(drawingFigureId);
      set({ pendingSyncIds: next });
    }
  },

  acknowledgeSourceRemoved: async (matterId, drawingFigureId) => {
    set({ mutationError: null });
    try {
      const r = await acknowledgeDrawingSourceRemoved(matterId, drawingFigureId);
      await useDrawingStore.getState().refreshLists(matterId);
      return r.figure;
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const message = (typeof detail === 'string' && detail)
        || (typeof detail === 'object' && detail !== null && (detail as { detail?: string }).detail)
        || (e as { message?: string })?.message
        || 'Acknowledge failed.';
      set({ mutationError: String(message) });
      return null;
    }
  },

  // ── Canvas + tools (slice 6C) ─────────────────────────────────
  setActiveTool: (tool) =>
    // Switching to a non-numeral tool cancels any pending
    // "place →" so a click on a different tool doesn't
    // accidentally drop a chip (Cue Part 5 carry-over from
    // BUG-030).
    // Phase 3 — placementMode/pendingNumeralId/pendingNumeralLabel
    // were cleared here when the drafter switched away from the
    // numeral tool; the # tool is gone, so the reset is too.
    set({ activeTool: tool }),
  setZoom: (level) => {
    // BUG-019e — clamp 0.25 .. 4.0 (25% .. 400%) per the spec.
    // Toolbar +/- buttons step in 10% increments via a 1.10x
    // multiplier; pinch / wheel still passes through untouched.
    const clamped = Math.max(0.25, Math.min(4, level));
    set({ zoomLevel: clamped });
  },
  loadCanvasFor: async (matterId, figureId, force) => {
    const cache = useDrawingStore.getState().canvasPrimitives;
    if (!force && Array.isArray(cache[figureId])) return;
    // FT-008 (C1) — in-flight dedup. Re-vectorize used to fire
    // three concurrent GET /canvas requests because three different
    // call sites (DrawingPage's figure-switch effect, Canvas's
    // post-vectorize forced reload, and the cross-tab focus
    // refetch) could all race for the same figure inside the same
    // event-loop turn. The cache check above guards against
    // SEQUENTIAL duplicates but not concurrent ones — the cache
    // hasn't been populated yet when the second/third caller
    // looks. The module-scoped in-flight map below collapses
    // concurrent loads into a single network request: the first
    // caller starts the GET, every subsequent caller (until the
    // promise settles) awaits the same promise. Brief acceptance
    // gate: exactly 2 GET /canvas per Re-vectorize cycle.
    const inflight = _inflightCanvasLoads[figureId];
    if (inflight) {
      await inflight;
      return;
    }
    const work = (async () => {
      try {
        const r = await getDrawingCanvas(matterId, figureId);
        if (Array.isArray((r as { primitives?: unknown }).primitives)) {
          const primitives = (r as { primitives: CanvasPrimitive[] }).primitives;
          // HR-P2.2 — cache the server's figure_updated_at so the
          // focus-refetch comparison can detect another tab's
          // writes. Falls back to null when the server response
          // omits it (older deploy). The same cache slot is
          // updated on every successful flush.
          const updatedAt = (r as { figure_updated_at?: string | null })
            .figure_updated_at ?? null;
          set((s) => ({
            canvasPrimitives: {
              ...s.canvasPrimitives, [figureId]: primitives,
            },
            figureUpdatedAtCache: {
              ...s.figureUpdatedAtCache, [figureId]: updatedAt,
            },
          }));
        }
      } catch (e: unknown) {
        const detail = (e as { response?: { data?: { detail?: unknown } } })
          ?.response?.data?.detail;
        const message = (typeof detail === 'string' && detail)
          || (e as { message?: string })?.message
          || 'Could not load canvas state.';
        set({ mutationError: String(message) });
      } finally {
        delete _inflightCanvasLoads[figureId];
      }
    })();
    _inflightCanvasLoads[figureId] = work;
    await work;
  },
  upsertCanvasPrimitive: (matterId, figureId, primitive) => {
    // Local state mutation first — the canvas re-renders before the
    // POST lands. The auto-save queue collapses bursts and eventually
    // syncs to the backend. Snapshot for undo unless we're inside a
    // mutation group (drag burst).
    if (!useDrawingStore.getState().isMutationGrouped) {
      _snapshotBefore(figureId);
    }
    let assignedId = primitive.id;
    set((s) => {
      const list = s.canvasPrimitives[figureId] || [];
      let next: CanvasPrimitive[];
      if (primitive.id) {
        const idx = list.findIndex((p) => p.id === primitive.id);
        if (idx >= 0) {
          const merged: CanvasPrimitive = {
            ...list[idx],
            primitive_type: primitive.primitive_type,
            primitive_data_json: primitive.primitive_data_json,
            z_index: primitive.z_index ?? list[idx].z_index,
            drawing_numeral_id: primitive.drawing_numeral_id ?? list[idx].drawing_numeral_id,
            updated_at: new Date().toISOString(),
          };
          next = [...list.slice(0, idx), merged, ...list.slice(idx + 1)];
        } else {
          next = [
            ...list,
            _materialiseLocalPrimitive(figureId, primitive, primitive.id),
          ];
        }
      } else {
        // Mint a deterministic-looking placeholder id so the local
        // re-render stays stable. The backend assigns the real UUID
        // on the next flush; the queue carries the placeholder so we
        // can swap it on response.
        assignedId = `local-${Math.random().toString(36).slice(2, 12)}`;
        next = [
          ...list,
          _materialiseLocalPrimitive(figureId, primitive, assignedId),
        ];
      }
      return { canvasPrimitives: { ...s.canvasPrimitives, [figureId]: next } };
    });
    enqueueMutation({
      kind: 'upsert',
      matterId,
      figureId,
      // Strip the local-* placeholder before sending to the backend.
      // ``upsertCanvasPrimitive`` mints a placeholder id so the local
      // re-render stays stable across React reconciliations, but the
      // backend has no row with that id — its UPSERT path treats a
      // present id as "UPDATE WHERE id = ?" and silently affects
      // 0 rows when the id doesn't exist, which is how freshly-
      // placed primitives never landed in the DB. Sending undefined
      // routes the backend down the INSERT branch + assigns a real
      // UUID; ``setOnFlushApplied`` below swaps the placeholder for
      // the real id once the response lands. The ``replace_all``
      // path (undo/redo) already does this strip on its side
      // (drawingStore.ts:911,943) — this aligns the two paths.
      primitive: {
        ...primitive,
        id: assignedId && assignedId.startsWith('local-')
          ? undefined
          : (primitive.id ?? assignedId),
      },
    });
    return assignedId || '';
  },
  removeCanvasPrimitive: (matterId, figureId, primitiveId) => {
    if (!useDrawingStore.getState().isMutationGrouped) {
      _snapshotBefore(figureId);
    }
    set((s) => {
      const list = s.canvasPrimitives[figureId] || [];
      const next = list.filter((p) => p.id !== primitiveId);
      return { canvasPrimitives: { ...s.canvasPrimitives, [figureId]: next } };
    });
    enqueueMutation({ kind: 'delete', matterId, figureId, primitiveId });
  },
  deleteSelectedPrimitives: (matterId, figureId, primitiveIds) => {
    if (!primitiveIds.length) return;
    const state = useDrawingStore.getState();
    state.beginCanvasMutationGroup(matterId, figureId);
    try {
      for (const id of primitiveIds) {
        state.removeCanvasPrimitive(matterId, figureId, id);
      }
    } finally {
      state.endCanvasMutationGroup();
    }
  },
  replaceCanvasState: (matterId, figureId, primitives) => {
    if (!useDrawingStore.getState().isMutationGrouped) {
      _snapshotBefore(figureId);
    }
    set((s) => {
      const next: CanvasPrimitive[] = primitives.map(
        (p) => _materialiseLocalPrimitive(figureId, p, p.id),
      );
      return { canvasPrimitives: { ...s.canvasPrimitives, [figureId]: next } };
    });
    enqueueMutation({ kind: 'replace_all', matterId, figureId, primitives });
  },
  // ── Undo / redo (slice 6F) ─────────────────────────────────────
  beginCanvasMutationGroup: (matterId, figureId) => {
    // Capture a snapshot before the drag/group starts; subsequent
    // mutations in the group skip per-call snapshots.
    _snapshotBefore(figureId);
    set({ isMutationGrouped: true });
  },
  endCanvasMutationGroup: () => set({ isMutationGrouped: false }),
  undoCanvas: (matterId, figureId) => {
    const stack = useDrawingStore.getState().undoStacks[figureId];
    if (!stack || stack.past.length === 0) return;
    const current = useDrawingStore.getState().canvasPrimitives[figureId] || [];
    const previous = stack.past[stack.past.length - 1];
    set((s) => ({
      undoStacks: {
        ...s.undoStacks,
        [figureId]: {
          past: stack.past.slice(0, -1),
          future: [_clonePrimitives(current), ...stack.future].slice(0, _UNDO_CAP),
        },
      },
      canvasPrimitives: {
        ...s.canvasPrimitives,
        [figureId]: _clonePrimitives(previous),
      },
    }));
    _persistUndoStacks();
    enqueueMutation({
      kind: 'replace_all',
      matterId,
      figureId,
      primitives: previous.map((p) => ({
        id: p.id.startsWith('local-') ? undefined : p.id,
        primitive_type: p.primitive_type,
        primitive_data_json: p.primitive_data_json,
        z_index: p.z_index,
        drawing_numeral_id: p.drawing_numeral_id,
      })),
    });
  },
  redoCanvas: (matterId, figureId) => {
    const stack = useDrawingStore.getState().undoStacks[figureId];
    if (!stack || stack.future.length === 0) return;
    const current = useDrawingStore.getState().canvasPrimitives[figureId] || [];
    const next = stack.future[0];
    set((s) => ({
      undoStacks: {
        ...s.undoStacks,
        [figureId]: {
          past: [...stack.past, _clonePrimitives(current)].slice(-_UNDO_CAP),
          future: stack.future.slice(1),
        },
      },
      canvasPrimitives: {
        ...s.canvasPrimitives,
        [figureId]: _clonePrimitives(next),
      },
    }));
    _persistUndoStacks();
    enqueueMutation({
      kind: 'replace_all',
      matterId,
      figureId,
      primitives: next.map((p) => ({
        id: p.id.startsWith('local-') ? undefined : p.id,
        primitive_type: p.primitive_type,
        primitive_data_json: p.primitive_data_json,
        z_index: p.z_index,
        drawing_numeral_id: p.drawing_numeral_id,
      })),
    });
  },
  canUndo: (figureId) => {
    const stack = useDrawingStore.getState().undoStacks[figureId];
    return !!stack && stack.past.length > 0;
  },
  canRedo: (figureId) => {
    const stack = useDrawingStore.getState().undoStacks[figureId];
    return !!stack && stack.future.length > 0;
  },

  // ── Cross-component bridges (slice 6G) ───────────────────────
  requestNumeralPlaceAt: (x, y) =>
    set({ pendingPlaceRequest: { x, y, ts: Date.now() } }),
  clearNumeralPlaceRequest: () => set({ pendingPlaceRequest: null }),
  requestNumeralFocus: (figureId, primitiveId) =>
    set({
      numeralFocusRequest: { figureId, primitiveId, ts: Date.now() },
    }),
  clearNumeralFocusRequest: () => set({ numeralFocusRequest: null }),
  // ── Cue Part 2 — selection + placement actions ─────────────────
  setSelectedIds: (ids) =>
    set({ selectedPrimitiveIds: Array.from(new Set(ids)) }),

  bumpSvgRefresh: (figureId) => set((state) => ({
    svgRefreshByFigure: {
      ...state.svgRefreshByFigure,
      [figureId]: (state.svgRefreshByFigure[figureId] ?? 0) + 1,
    },
  })),
  clearSelection: () => set({ selectedPrimitiveIds: [] }),
  deletePrimitives: (matterId, figureId, primitiveIds) => {
    if (!primitiveIds.length) return;
    // BUG-010 — single set() batch. The previous implementation
    // looped over ``removeCanvasPrimitive`` which itself called
    // ``set(...)`` once per id; lasso-deleting 10-16 primitives
    // produced 10-16 separate React render cycles (matching the
    // "10-16x state changes per click" the BUG-010 report
    // flagged). Now the local-cache update happens in ONE set(),
    // the persistence queue is appended in a tight non-state
    // loop, and the selection clear rides the same set() — total
    // 1 React render per lasso delete regardless of selection
    // size.
    const idSet = new Set(primitiveIds);
    _snapshotBefore(figureId);
    set((s) => {
      const list = s.canvasPrimitives[figureId] || [];
      const next = list.filter((p) => !idSet.has(p.id));
      // Preserve the original list reference when nothing
      // matched (defensive — caller already gated on
      // primitiveIds.length above, but a stale id list could
      // produce a no-op). Avoids spurious downstream renders.
      return {
        canvasPrimitives: next.length === list.length
          ? s.canvasPrimitives
          : { ...s.canvasPrimitives, [figureId]: next },
        selectedPrimitiveIds: [],
      };
    });
    // Mutation-queue enqueue is non-state (writes to the module-
    // level autosave buffer). Loop here outside the set() so the
    // backend still receives one delete per primitive.
    for (const id of primitiveIds) {
      enqueueMutation({ kind: 'delete', matterId, figureId, primitiveId: id });
    }
  },
  // Phase 3 — startNumeralPlacement / cancelNumeralPlacement /
  // placeNumeral implementations retired with the # tool. Numerals
  // enter the registry via Analyze (vision extraction) or auto-map
  // (text-annotation pass landing in Phase 4); the chip-arming
  // flow is gone.

  flushCanvasNow: async () => {
    cancelPendingFlush();
    const r = await flushNow();
    // Reconciliation now flows through the shared helper so the
    // debounced auto-save path (timer at drawingAutoSave.ts:141)
    // and the manual flushCanvasNow path use the SAME code. The
    // helper is registered as ``setOnFlushApplied`` below + is
    // re-invoked here for the manual path so the swap is in
    // place by the time this awaitable resolves (callers like
    // DrawingPage's figure-switch effect rely on that).
    if (r.appliedPrimitives && r.appliedPrimitives.length > 0) {
      _reconcileAppliedPrimitives(r.appliedPrimitives);
    }
    // HR-P2.2 — stash the latest figure_updated_at per figureId
    // we wrote in this batch. Even when the swap-id step above
    // is a no-op (every primitive already had a server id), the
    // updated_at moved forward and must be cached so the focus
    // refetch comparison works.
    if (r.figureUpdatedAtByFigure
        && Object.keys(r.figureUpdatedAtByFigure).length > 0) {
      set((s) => ({
        figureUpdatedAtCache: {
          ...s.figureUpdatedAtCache,
          ...r.figureUpdatedAtByFigure,
        },
      }));
    }
    // BUG-033 — surface the "N rejected" toast once per flush so
    // drafters know the queue self-healed (they don't need to
    // refresh).
    if (r.droppedBadCount && r.droppedBadCount > 0) {
      const detail = r.lastDropError
        ? ` Last error: ${r.lastDropError.slice(0, 120)}`
        : '';
      set({
        mutationError:
          `${r.droppedBadCount} canvas change${r.droppedBadCount === 1 ? '' : 's'} `
          + `were rejected by the server and discarded.${detail}`,
      });
    }
  },

  // ── Numeral registry (slice 6B) ───────────────────────────────
  createNumeralRegistryRow: async (matterId, body) => {
    set({ mutationError: null });
    try {
      const r = await createDrawingNumeral(matterId, body);
      // Fold the new row into the cached list so the Numerals tab
      // renders without an extra GET.
      set((s) => ({ numerals: [...s.numerals, r.numeral] }));
      return r.numeral;
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      // numeral_exists is the cross-figure-consistency 409 — let the
      // caller handle it (they get the existing row + placed_on
      // figure ids in the detail). Surface the message inline; the
      // caller still receives null.
      const message = (typeof detail === 'string' && detail)
        || (typeof detail === 'object' && detail !== null
            && (detail as { detail?: string }).detail)
        || (e as { message?: string })?.message
        || 'Could not create numeral.';
      set({ mutationError: String(message) });
      return null;
    }
  },
  patchNumeralRegistryRow: async (matterId, numeralId, body) => {
    set({ mutationError: null });
    try {
      const r = await patchDrawingNumeral(matterId, numeralId, body);
      set((s) => ({
        numerals: s.numerals.map((n) => n.id === numeralId ? r.numeral : n),
      }));
      return r.numeral;
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      const message = (typeof detail === 'string' && detail)
        || (e as { message?: string })?.message
        || 'Could not update numeral.';
      set({ mutationError: String(message) });
      return null;
    }
  },
  deleteNumeralRegistryRow: async (matterId, numeralId) => {
    set({ mutationError: null });
    try {
      await deleteDrawingNumeral(matterId, numeralId);
      // BUG-010 — preserve unchanged-figure references. Previously
      // every figure's primitive array was rebuilt by ``.map(...)``
      // even when none of its primitives referenced the deleted
      // numeral. Every Canvas / RasterWithOverlay / ReferencePanel
      // subscriber to ``canvasPrimitives[figure_id]`` for any figure
      // saw a new array reference and re-rendered, broadening the
      // cascade — N figures × M chips of lookup work per delete.
      // Now: only rebuild the lists that actually contain a chip
      // bound to the about-to-be-orphaned numeral; leave every
      // other figure's array reference untouched so its subscribers
      // skip the render entirely.
      set((s) => {
        const nextPrimitives: typeof s.canvasPrimitives = {};
        let primitivesChanged = false;
        for (const [fid, list] of Object.entries(s.canvasPrimitives)) {
          let mutated = false;
          const next = list.map((p) => {
            if (p.drawing_numeral_id === numeralId) {
              mutated = true;
              return { ...p, drawing_numeral_id: null };
            }
            return p;
          });
          if (mutated) {
            nextPrimitives[fid] = next;
            primitivesChanged = true;
          } else {
            // Same reference — Zustand selectors keyed on this
            // figure's array will Object.is-equal and skip render.
            nextPrimitives[fid] = list;
          }
        }
        return {
          numerals: s.numerals.filter((n) => n.id !== numeralId),
          // If no primitive anywhere referenced the numeral, keep
          // the top-level reference too so Canvas (which
          // subscribes to the whole canvasPrimitives map) also
          // skips the render. Numeral-only deletions are common
          // (registry rows with no canvas placement yet) so this
          // is worth the extra branch.
          canvasPrimitives: primitivesChanged
            ? nextPrimitives
            : s.canvasPrimitives,
        };
      });
      return true;
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      const message = (typeof detail === 'string' && detail)
        || (e as { message?: string })?.message
        || 'Could not delete numeral.';
      set({ mutationError: String(message) });
      return false;
    }
  },

  linkTermToNumeral: async (matterId, termMappingId, numeralId) => {
    // PD-002 — drafter-driven term ↔ numeral binding. Server
    // returns the updated term_mappings row; reflect both the
    // termMappings array AND the matching drawing_numerals
    // row's bound_claim_term in place so the rail's three-state
    // badge (linked / mapped / unmapped) flips immediately
    // without a refetch. Mirrors the auto-map high-confidence
    // path's reverse-pointer write on the backend.
    set({ mutationError: null });
    try {
      const res = await patchDrawingTermMapping(
        matterId,
        termMappingId,
        numeralId === null ? { unbind: true } : { drawing_numeral_id: numeralId },
      );
      const updated = res.term_mapping;
      set((s) => {
        const nextTermMappings = s.termMappings.map((m) =>
          m.id === termMappingId ? updated : m,
        );
        let nextNumerals = s.numerals;
        if (numeralId !== null) {
          // Mirror the reverse-pointer write so the Numerals tab's
          // badge flips from "unmapped" to "linked" without
          // waiting for the next refetch.
          nextNumerals = s.numerals.map((n) => {
            if (n.id !== numeralId) return n;
            // Defensive — only update when there isn't already a
            // binding (matches the backend's NULL-only WHERE).
            if (n.bound_claim_id) return n;
            return {
              ...n,
              bound_claim_id: updated.claim_id,
              bound_claim_term: updated.claim_term,
            };
          });
        }
        return {
          termMappings: nextTermMappings,
          numerals: nextNumerals,
        };
      });
      return true;
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      const message =
        (typeof detail === 'string' && detail)
        || (typeof detail === 'object'
          && detail
          && (detail as { detail?: string }).detail)
        || (e as { message?: string })?.message
        || 'Could not link claim term to numeral.';
      set({ mutationError: String(message) });
      return false;
    }
  },

  discardFigure: async (matterId, drawingFigureId) => {
    set({ mutationError: null });
    try {
      await discardDrawingFigure(matterId, drawingFigureId);
      // BUG-002 — surgical atomic update. The previous
      // implementation called ``refreshLists`` which fires 4
      // parallel GETs + 4 separate ``set(...)`` calls,
      // replacing every figures / sourceFigures / numerals /
      // termMappings array reference. Pre-BUG-001 (PostHog
      // session_recording disabled at init) that produced a
      // 30-120s compositor freeze; even with BUG-001 fixed the
      // churn was unnecessary. Backend already cascade-deletes
      // drawing_canvas_state + drawing_term_mappings tied to
      // the figure (drawing.py:_discard_figure), so the local
      // cache update is purely a filter pass — no GET round-
      // trips needed. Same SINGLE set() collapses the next-
      // active-figure pick so the UI doesn't go blank-canvas
      // (which the original ``set({activeFigureId: null})``
      // path felt like "navigate-to-home" from the drafter's
      // perspective).
      set((s) => {
        const wasActive = s.activeFigureId === drawingFigureId;
        const nextFigures = s.figures.filter(
          (f) => f.id !== drawingFigureId,
        );
        // BUG-002 — pick the NEXT available figure as the new
        // active. Going to null left the canvas blank with no
        // hint that any figure existed; drafters reported it
        // as "the page disappeared" after delete. Fall through
        // to null only when the matter has no figures left.
        let nextActive = s.activeFigureId;
        if (wasActive) {
          nextActive = nextFigures.length > 0 ? nextFigures[0].id : null;
        }
        // Surgical numerals filter — drop any registry row
        // whose only placement was this figure. Numerals
        // referenced by other figures stay; the chip on the
        // discarded figure is already gone because backend
        // cascade-deleted ``drawing_canvas_state`` rows for the
        // figure. ``figure_ids`` on the numeral row is the
        // server-derived placement list; we recompute by
        // dropping the deleted figureId from each row's list
        // and keeping rows that still have ≥1 placement OR are
        // unplaced (figure_ids was empty before).
        const nextNumerals = s.numerals
          .map((n) => {
            const figs = Array.isArray(n.figure_ids) ? n.figure_ids : [];
            if (!figs.includes(drawingFigureId)) return n;
            return { ...n, figure_ids: figs.filter((f) => f !== drawingFigureId) };
          });
        // Drop the deleted figure's primitive list entry — it's
        // gone from the backend; any reference to its key would
        // be stale. Other figures' lists keep their original
        // array reference (no spurious re-renders).
        const nextPrimitives = { ...s.canvasPrimitives };
        delete nextPrimitives[drawingFigureId];
        // Term mappings — backend already cascade-deleted via
        // drawing_term_mappings.figure_id_override or the
        // figure FK; drop locally to match. The previous
        // refreshLists re-fetched these too, but a filter
        // pass is equivalent server-truth-wise.
        const nextTermMappings = s.termMappings.filter(
          (t) => t.figure_id_override !== drawingFigureId,
        );
        return {
          figures: nextFigures,
          activeFigureId: nextActive,
          numerals: nextNumerals,
          canvasPrimitives: nextPrimitives,
          termMappings: nextTermMappings,
        };
      });
      return true;
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const message = (typeof detail === 'string' && detail)
        || (e as { message?: string })?.message
        || 'Discard failed.';
      set({ mutationError: String(message) });
      return false;
    }
  },

  reset: () => {
    // BUG-060 — kill the polling interval on matter switch so a
    // background job belonging to matter A doesn't keep firing
    // GETs while the drafter works on matter B.
    _stopGenerationJobPoll();
    set({
      hydrationStatus: 'idle',
      lockedReason: null,
      rollup: null,
      errorMessage: null,
      hydratedMatterId: null,
      figures: [],
      sourceFigures: [],
      activeFigureId: null,
      numerals: [],
      termMappings: [],
      pendingImportSourceIds: new Set<number>(),
      uploading: false,
      pendingSyncIds: new Set<string>(),
      mutationError: null,
      activeTool: 'cursor',
      zoomLevel: 1,
      canvasPrimitives: {},
      figureUpdatedAtCache: {},
      saveStatus: 'idle',
      pendingMutationCount: 0,
      lastSavedAt: null,
      // referencePanelTab intentionally NOT reset — sticky preference
      // survives matter switches and reloads, which is the contract
      // the brief specifies for Phase 4.
      generationModalOpen: false,
      // BUG-060 — clear in-flight generation job on matter switch.
      // Stops the module-scoped polling loop too via the action.
      activeGenerationJobId: null,
      generationJobStatus: 'idle',
      generationJobState: null,
      generationJobMatterId: null,
      analyzeModalFigureId: null,
      claimEditModalClaimId: null,
      latestVerifyByFigure: {},
      verifyingFigureId: null,
      toastMessage: null,
      // Cue Part 2 — reset selection state on matter switch.
      // The placementMode / pendingNumeralId / pendingNumeralLabel
      // fields documented at the comment above line ~203 were
      // retired with the # canvas tool; the reset payload here
      // is dropped to match the current DrawingStore type
      // (TS2345 build error otherwise — see commit body of
      // build-fix patch).
      selectedPrimitiveIds: [],
      svgRefreshByFigure: {},
    });
  },
}));


// ─── BUG-060 — module-scoped generation-job poll loop ─────────────
//
// Lives at module scope (mirrors specStore._startGEPoll) so a modal
// close doesn't tear down the polling interval. Stops on terminal
// status, matter switch, or explicit clearGenerationJob.

const _GEN_POLL_INTERVAL_MS = 2_000;
const _GEN_TERMINAL_STATUSES: ReadonlySet<GenerateAiJobState['status']> = new Set([
  'completed', 'failed', 'cancelled',
]);
let _genPollTimer: ReturnType<typeof setInterval> | null = null;

function _stopGenerationJobPoll(): void {
  if (_genPollTimer !== null) {
    clearInterval(_genPollTimer);
    _genPollTimer = null;
  }
}

function _startGenerationJobPoll(): void {
  // Idempotent — re-starting while a poll already runs is a no-op.
  if (_genPollTimer !== null) return;
  if (typeof window === 'undefined') return;

  const tick = async (): Promise<void> => {
    const state = useDrawingStore.getState();
    const jobId = state.activeGenerationJobId;
    const matterId = state.generationJobMatterId;
    if (!jobId || !matterId) {
      _stopGenerationJobPoll();
      return;
    }
    try {
      const next = await getGenerateAiJob(matterId, jobId);
      // Re-read state after the await — drafter may have switched
      // matters or cleared the job mid-flight.
      const cur = useDrawingStore.getState();
      if (cur.activeGenerationJobId !== jobId) {
        _stopGenerationJobPoll();
        return;
      }
      cur.updateGenerationProgress(next);
      if (_GEN_TERMINAL_STATUSES.has(next.status)) {
        _stopGenerationJobPoll();
      }
    } catch {
      // Transient network blip — keep polling. Sustained failure
      // surfaces via the modal's progress view if open; the header
      // pill stays on the last-known status.
    }
  };

  // Fire immediately + every 2s. The first tick warms the cache so
  // the corner pill / modal progress view both render with real
  // data on first paint.
  void tick();
  _genPollTimer = setInterval(() => { void tick(); }, _GEN_POLL_INTERVAL_MS);
}


// ─── Auto-save queue → store bridge (slice 6C) ────────────────────
//
// The autosave layer in src/lib/drawingAutoSave is intentionally
// decoupled from Zustand; we wire it in here so the saveStatus +
// pendingMutationCount + lastSavedAt fields stay in sync with the
// queue's lifecycle. DrawingPage calls subscribeToAutoSave on mount
// (via the store action below) and unsubscribes on unmount.

/**
 * After every successful flush (manual via ``flushCanvasNow`` OR
 * debounced via the auto-save timer), swap any ``local-*``
 * placeholder ids in the cache for the backend-assigned real UUIDs.
 * Same primitive_type matching the original swap loop used —
 * deliberately conservative so a flush that returns server's full
 * primitives list (which includes already-real-id rows) doesn't
 * accidentally overwrite settled rows.
 *
 * Without this hook the debounced path leaves placeholders stranded:
 * the strip-local-on-enqueue fix above means the backend INSERTs
 * correctly on first save, but every subsequent edit on the same
 * primitive ships ``id: undefined`` again (because the store still
 * holds the placeholder) and the backend INSERTs a duplicate row.
 */
function _reconcileAppliedPrimitives(
  appliedPrimitives: CanvasPrimitive[],
): void {
  useDrawingStore.setState((s) => {
    const next: Record<string, CanvasPrimitive[]> = { ...s.canvasPrimitives };
    for (const persisted of appliedPrimitives) {
      const fid = persisted.drawing_figure_id;
      const list = next[fid] || [];
      const localIdx = list.findIndex(
        (p) => p.id.startsWith('local-')
          && p.primitive_type === persisted.primitive_type,
      );
      if (localIdx >= 0) {
        next[fid] = [
          ...list.slice(0, localIdx),
          persisted,
          ...list.slice(localIdx + 1),
        ];
      }
    }
    return { canvasPrimitives: next };
  });
}

// Module-level registration — runs once when this file is first
// imported. Idempotent: if the module re-evaluates (HMR), the
// setter overwrites with the same function reference. Tests that
// need to intercept can call setOnFlushApplied(null) in their
// teardown.
setOnFlushApplied(_reconcileAppliedPrimitives);

// SEC-F03 (Phase 27 3-G) — wire the IDB persist-failed toast.
// Fires when the drawing auto-save queue's IDB write rejects
// (quota exceeded / corruption / transaction inactive). Without
// this hook the queue silently truncated and drafters lost
// work; now the showToast action surfaces the failure so they
// can investigate available storage before continuing.
setOnPersistFailed((reason: string) => {
  try {
    useDrawingStore.getState().showToast(reason);
  } catch {
    // showToast may not be wired yet during very early boot;
    // swallow rather than crash the queue lifecycle.
  }
});

let _queueUnsubscribe: (() => void) | null = null;

export function subscribeToAutoSaveQueue(): () => void {
  if (_queueUnsubscribe) return _queueUnsubscribe;
  const unsub = subscribeToQueue((qs) => {
    const status: 'idle' | 'saving' | 'saved' | 'offline' = (() => {
      if (qs.offline) return 'offline';
      if (qs.flushing) return 'saving';
      if (qs.lastSavedAt && qs.pending.length === 0) return 'saved';
      return qs.pending.length > 0 ? 'saving' : 'idle';
    })();
    useDrawingStore.setState({
      saveStatus: status,
      pendingMutationCount: qs.pending.length,
      lastSavedAt: qs.lastSavedAt,
    });
  });
  _queueUnsubscribe = () => {
    unsub();
    _queueUnsubscribe = null;
  };
  // Seed initial values from the current queue state.
  const seed = getQueueState();
  useDrawingStore.setState({
    saveStatus: seed.offline
      ? 'offline'
      : (seed.flushing ? 'saving' : (seed.lastSavedAt ? 'saved' : 'idle')),
    pendingMutationCount: seed.pending.length,
    lastSavedAt: seed.lastSavedAt,
  });
  return _queueUnsubscribe;
}

/** DrawingPage calls this on mount per matter. Restores any
 * persisted offline queue from a prior session and re-fires the
 * flush. Returns the count of restored mutations so the page can
 * surface a toast. */
export async function hydrateAutoSaveForMatter(matterId: string): Promise<number> {
  return hydratePendingMutations(matterId);
}

// ─── Local primitive helpers ──────────────────────────────────────

function _materialiseLocalPrimitive(
  figureId: string,
  input: CanvasPrimitiveInput,
  forcedId?: string,
): CanvasPrimitive {
  const now = new Date().toISOString();
  return {
    id: forcedId || `local-${Math.random().toString(36).slice(2, 12)}`,
    drawing_figure_id: figureId,
    primitive_type: input.primitive_type,
    primitive_data_json: input.primitive_data_json,
    z_index: input.z_index ?? 0,
    drawing_numeral_id: input.drawing_numeral_id ?? null,
    created_at: now,
    updated_at: now,
  };
}

// ─── Undo / redo helpers (slice 6F → HR-P1.3 IDB migration) ──────

const _UNDO_CAP = 50;
// Legacy localStorage key (slice 6F). Kept as a constant so the
// HR-P1.3 migration in idbUndoStack.ts can read it once during
// hydration, then drop it. New writes never touch localStorage.
const _UNDO_STORAGE_KEY_LEGACY = 'drawings:undoStacks';

function _clonePrimitives(list: CanvasPrimitive[]): CanvasPrimitive[] {
  // Structured clone via JSON — primitives are pure data
  // (no cycles, no Date/Map). Fast enough at the slice 6F snapshot
  // sizes (≤ a few hundred primitives per figure × 50 stack slots).
  return JSON.parse(JSON.stringify(list)) as CanvasPrimitive[];
}

function _snapshotBefore(figureId: string): void {
  const current = useDrawingStore.getState().canvasPrimitives[figureId] || [];
  useDrawingStore.setState((s) => {
    const stack = s.undoStacks[figureId] || { past: [], future: [] };
    const past = [...stack.past, _clonePrimitives(current)].slice(-_UNDO_CAP);
    return {
      undoStacks: {
        ...s.undoStacks,
        // Mutations after a snapshot invalidate the redo stack —
        // standard undo/redo semantics (the brief expects this).
        [figureId]: { past, future: [] },
      },
    };
  });
  _persistUndoStacks();
}

// HR-P1.3 — undo stacks persist to IndexedDB instead of
// localStorage. The legacy localStorage key (`_UNDO_STORAGE_KEY_LEGACY`)
// is migrated on first hydrate then dropped. Quota errors during
// persist drop the OLDEST snapshot per figure (keep recent
// edits) and fire ``showToast`` once so the drafter knows.
function _persistUndoStacks(): void {
  if (typeof window === 'undefined') return;
  void import('@/lib/idbUndoStack').then(
    ({ persistUndoStacksToIdb }) => persistUndoStacksToIdb(
      useDrawingStore.getState().undoStacks,
      {
        onQuotaFallback: () => {
          // Side-channel toast — independent of the per-figure
          // showToast helper because the quota error is a
          // matter-wide event. Fire only once per session via
          // the underlying showToast's identity-debounce.
          try {
            useDrawingStore.getState().showToast(
              'Older undo history was trimmed to free space.',
            );
          } catch { /* toast plumbing is best-effort */ }
        },
      },
    ),
  ).catch(() => {
    /* IDB unavailable, schema rejected, or quota exhausted
       even after shrinks — undo still works in-memory; reload
       won't restore. Production browsers grant ample IDB
       quota, so this is a degradation path, not the common
       case. */
  });
}

/** Read the persisted undo stacks on app boot. Called from the
 *  DrawingPage hydration path so undo survives a reload (brief
 *  §7). HR-P1.3: IDB is the canonical home; the slice 6F
 *  ``localStorage`` entry is migrated on first hit then
 *  dropped. Returns nothing (the function rehydrates the store
 *  in place); awaitable so test code can synchronise with the
 *  IDB transaction. */
export async function hydrateUndoStacksFromStorage(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const {
      loadUndoStacksFromIdb,
      migrateUndoStacksFromLocalStorage,
    } = await import('@/lib/idbUndoStack');
    const fromIdb = await loadUndoStacksFromIdb();
    if (fromIdb) {
      useDrawingStore.setState({ undoStacks: fromIdb });
      return;
    }
    // No IDB row yet — try migrating from the slice 6F
    // localStorage key. Returns null when there's nothing to
    // migrate; the store stays at its empty initial state.
    const migrated = await migrateUndoStacksFromLocalStorage();
    if (migrated) {
      useDrawingStore.setState({ undoStacks: migrated });
    }
  } catch {
    /* corrupt storage / IDB unavailable — start fresh */
  }
}


// ─── HR-P2.2 — cross-tab last-write-wins detection ───────────────


/** Refetch the canvas state for the active figure and compare
 *  the server's ``figure_updated_at`` against the client's
 *  cached value. When the server is ahead AND the client had a
 *  cached value (i.e. we wrote something in this tab), assume
 *  another tab wrote → toast + hydrate the new state.
 *
 *  No-op when ``window`` is undefined, when the cached value is
 *  null (fresh mount, nothing to compare against), or when the
 *  server's response carries no ``figure_updated_at`` (older
 *  deploy without HR-P2.2's response field).
 *
 *  Exported so the Canvas can call it from a focus event
 *  listener and tests can drive it directly. */
export async function checkForCrossTabWrite(
  matterId: string, figureId: string,
): Promise<{ refreshed: boolean; reason?: string }> {
  if (typeof window === 'undefined') {
    return { refreshed: false, reason: 'no-window' };
  }
  if (!matterId || !figureId) {
    return { refreshed: false, reason: 'no-target' };
  }
  // FT-008 (C1) — if a canvas load is already in flight for this
  // figure (e.g. fired by the post-vectorize forced reload that
  // ran milliseconds before this focus event), skip — the in-flight
  // load is already going to refresh the local cache. Without this
  // guard the focus listener piles a redundant GET on top of the
  // post-vectorize load, which is the 3rd request the brief
  // observed during Re-vectorize.
  if (_inflightCanvasLoads[figureId]) {
    return { refreshed: false, reason: 'load-in-flight' };
  }
  const state = useDrawingStore.getState();
  const cachedAt = state.figureUpdatedAtCache[figureId] ?? null;
  if (!cachedAt) {
    // Fresh mount — no local write to compare against. Skip the
    // toast (would surprise drafters who haven't done anything
    // yet) but still rehydrate so they see the latest state.
    try {
      await state.loadCanvasFor(matterId, figureId, true);
    } catch { /* swallow */ }
    return { refreshed: false, reason: 'no-prior-write' };
  }
  let r;
  try {
    r = await getDrawingCanvas(matterId, figureId);
  } catch {
    return { refreshed: false, reason: 'fetch-error' };
  }
  if (!Array.isArray((r as { primitives?: unknown }).primitives)) {
    return { refreshed: false, reason: 'locked-or-empty' };
  }
  const serverAt = (r as { figure_updated_at?: string | null })
    .figure_updated_at ?? null;
  if (!serverAt) {
    return { refreshed: false, reason: 'server-omitted-timestamp' };
  }
  if (serverAt <= cachedAt) {
    return { refreshed: false, reason: 'server-equal-or-stale' };
  }
  // Another tab wrote — surface the toast + hydrate the new
  // state so the drafter sees the latest primitives.
  const primitives = (r as { primitives: CanvasPrimitive[] }).primitives;
  useDrawingStore.setState((s) => ({
    canvasPrimitives: { ...s.canvasPrimitives, [figureId]: primitives },
    figureUpdatedAtCache: {
      ...s.figureUpdatedAtCache, [figureId]: serverAt,
    },
  }));
  try {
    state.showToast('Canvas updated in another tab — refreshing.');
  } catch { /* toast is best-effort */ }
  return { refreshed: true };
}
